import React, { useEffect, useState } from 'react';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import { useTranslation } from 'react-i18next';
import { CredentialResponse } from '@react-oauth/google';
import { useAppDispatch, useAppSelector } from '../../store';
import { changePassword } from '../../store/general/general-actions';
import { selectUser } from '../../store/general/general-slice-selectors';
import TextInput from '@common/components/Inputs/TextInput';
import CustomAlert from '@common/components/Feedback/CustomAlert';
import CustomSnackbar from '@common/components/Feedback/CustomSnackbar';
import HttpFetchStatus from '@common/enums/api/http-fetch-status';
import { isHttpError } from '@common/interfaces/api/http-error';
import { resetUserRestStatus } from 'store/general/general-slice';
import LoginVariant from '../../models/enums/login-variant';
import GoogleLoginInput from 'components/forms/GoogleLoginInput';
import FormHelperText from '@mui/material/FormHelperText';
import InputLabel from '@mui/material/InputLabel';
import { getPasswordStrength } from '../../utils/passwordStrength';
import PasswordInput from 'components/forms/PasswordInput';

const ChangePasswordForm: React.FC = () => {
  const { t } = useTranslation();

  const dispatch = useAppDispatch();

  const currentUser = useAppSelector((state) => selectUser(state));

  const [currentPassword, setCurrentPassword] = useState('');
  const [googleToken, setGoogleToken] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [otp, setOtp] = useState('');

  const isTwoFactorConfigured = currentUser.data?.isTwoFactorConfigured ?? false;
  const isGoogleLogin = currentUser.data?.lastLoginVariant === LoginVariant.Google;

  const resetForm = () => {
    setCurrentPassword('');
    setGoogleToken('');
    setNewPassword('');
    setOtp('');
  };

  useEffect(() => {
    if (currentUser.rest.postChangePasswordStatus === HttpFetchStatus.Success) {
      resetForm();
    }
  }, [currentUser.rest.postChangePasswordStatus]);

  const googleLoginSuccessHandler = (response: CredentialResponse) => {
    if (response.credential != null) {
      setGoogleToken(response.credential);
    }
  };

  const submitHandler = () => {
    dispatch(
      changePassword({
        currentPassword: isGoogleLogin ? undefined : currentPassword,
        googleToken: isGoogleLogin ? googleToken : undefined,
        newPassword,
        otp: isTwoFactorConfigured ? otp : undefined,
      }),
    );
  };

  const closeSuccessSnackbarHandler = () => {
    dispatch(
      resetUserRestStatus({
        postChangePasswordError: null,
        postChangePasswordStatus: HttpFetchStatus.Idle,
      }),
    );
  };

  const isSubmitDisabled =
    (isGoogleLogin ? googleToken.length === 0 : currentPassword.length === 0) ||
    newPassword.length === 0 ||
    !getPasswordStrength(newPassword).isValid ||
    (isTwoFactorConfigured && otp.length === 0) ||
    currentUser.rest.postChangePasswordStatus === HttpFetchStatus.Loading;

  return (
    <Stack spacing={1} width="100%">
      <InputLabel>{t('button-change-password')}</InputLabel>
      <FormHelperText>
        {isGoogleLogin
          ? t('form-help-text-change-password-3rd-party')
          : t('form-help-text-change-password')}
      </FormHelperText>
      {isGoogleLogin ? (
        <GoogleLoginInput
          onSuccess={googleLoginSuccessHandler}
          onError={() => setGoogleToken('')}
        />
      ) : (
        <PasswordInput
          name="currentPassword"
          password={currentPassword}
          onChange={(newValue) => setCurrentPassword(newValue)}
          label={t('input-label-current-password')}
        />
      )}
      <PasswordInput
        isSetField
        name="newPassword"
        password={newPassword}
        onChange={(newValue) => setNewPassword(newValue)}
        label={t('input-label-new-password')}
      />
      {isTwoFactorConfigured && (
        <TextInput
          label={t('input-label-authenticator-code')}
          value={otp}
          onChange={(event) => setOtp(event.target.value)}
        />
      )}
      {currentUser.rest.postChangePasswordError != null &&
        isHttpError(currentUser.rest.postChangePasswordError) && (
          <CustomAlert
            header={t('error-resetting-password')}
            textMessages={[t(currentUser.rest.postChangePasswordError.message)]}
            severity="error"
          />
        )}
      <Button onClick={submitHandler} variant="contained" disabled={isSubmitDisabled}>
        {t('button-change-password')}
      </Button>
      <CustomSnackbar
        open={currentUser.rest.postChangePasswordStatus === HttpFetchStatus.Success}
        title={t('success-change-password')}
        onClose={closeSuccessSnackbarHandler}
      />
    </Stack>
  );
};

export default ChangePasswordForm;
